import { SwitchRow } from '@components/SwitchRow/SwitchRow';
import formCss from '@lib/style/form.module.scss';
import { FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import type { ComposeFieldsProps } from '../../types';
import {
  describeReelRule,
  describeVideoRule,
  FACEBOOK_DESCRIPTION_MAX_LENGTH,
  FACEBOOK_KIND_LABELS,
  FACEBOOK_TITLE_MAX_LENGTH,
  type FacebookAccountCapabilities,
  type FacebookPostContent,
  type FacebookPostKind,
} from './index';

/** What each kind is, with the media rule from the Page's capabilities once read. */
const kindHint = (kind: FacebookPostKind, capabilities: FacebookAccountCapabilities | null) => {
  if (kind === 'reel') {
    return `Posted to the Page’s Reels, as a vertical short.${capabilities ? ` ${describeReelRule(capabilities)}` : ''}`;
  }
  return `A video post on the Page, any frame.${capabilities ? ` ${describeVideoRule(capabilities)}` : ''}`;
};

/**
 * Facebook's part of the compose form: the kind of post, then the title and
 * caption, the AI disclosure on a reel, and the Page draft switch. Switching
 * kind keeps the text; the disclosure is a reel's alone, so it goes with it.
 */
export function FacebookComposeFields({
  content,
  onChange,
  capabilities,
  disabled,
}: ComposeFieldsProps<FacebookPostContent, FacebookAccountCapabilities>) {
  const setKind = (kind: FacebookPostKind) => {
    if (kind === content.kind) return;
    const { platform, title, description, draft } = content;
    const text = {
      platform,
      ...(title !== undefined && { title }),
      ...(description !== undefined && { description }),
      draft,
    };
    onChange(kind === 'reel' ? { ...text, kind, aiGenerated: false } : { ...text, kind });
  };

  return (
    <div className={formCss.composeFields}>
      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Post as</span>
        <RadioGroup
          className={formCss.radioGroup}
          value={content.kind}
          onChange={(e) => setKind(e.target.value === 'reel' ? 'reel' : 'video')}
        >
          {(['reel', 'video'] as const).map((kind) => (
            <FormControlLabel
              key={kind}
              value={kind}
              control={<Radio />}
              disabled={disabled}
              className={formCss.radioOption}
              label={
                <>
                  <span>{FACEBOOK_KIND_LABELS[kind]}</span>
                  <span className={formCss.fieldHint}>{kindHint(kind, capabilities)}</span>
                </>
              }
            />
          ))}
        </RadioGroup>
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Title</span>
        <TextField
          className={formCss.textInput}
          placeholder="Optional"
          value={content.title ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...content, title: e.target.value || undefined })}
          helperText={`${(content.title ?? '').length.toLocaleString()} / ${FACEBOOK_TITLE_MAX_LENGTH.toLocaleString()}`}
          slotProps={{ htmlInput: { maxLength: FACEBOOK_TITLE_MAX_LENGTH } }}
        />
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Caption</span>
        <TextField
          className={formCss.textInput}
          multiline
          minRows={3}
          placeholder="Say something about this post #hashtags @mentions"
          value={content.description ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...content, description: e.target.value || undefined })}
          helperText={`${(content.description ?? '').length.toLocaleString()} / ${FACEBOOK_DESCRIPTION_MAX_LENGTH.toLocaleString()}`}
          slotProps={{ htmlInput: { maxLength: FACEBOOK_DESCRIPTION_MAX_LENGTH } }}
        />
      </div>

      <div className={formCss.switchStack}>
        {content.kind === 'reel' && (
          <SwitchRow
            label="AI-generated content"
            hint="Adds Meta’s AI label to the reel."
            checked={content.aiGenerated}
            disabled={disabled}
            onChange={(aiGenerated) => onChange({ ...content, aiGenerated })}
          />
        )}
        <SwitchRow
          label="Save as a draft on the Page"
          hint="Stays unpublished on the Page, to finish from its own tools. The title and caption still apply."
          checked={content.draft}
          disabled={disabled}
          onChange={(draft) => onChange({ ...content, draft })}
        />
      </div>
    </div>
  );
}
